import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Grid, MenuItem, Select, FormControl, InputLabel,
  Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { Add, Edit, Delete, Refresh, FilterList, TableRows, CalendarMonth, OpenInNew } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import { surgeryService } from '../../services/api';
import TopBar from '../../components/TopBar';
import SurgeryFormDialog, { STATUS_OPTIONS } from '../../components/SurgeryFormDialog';

const STATUS_COLORS = {
  Scheduled:  { bg: '#e3f2fd', border: '#1565c0', text: '#1565c0' },
  InProgress: { bg: '#fff3e0', border: '#e65100', text: '#e65100' },
  Completed:  { bg: '#e8f5e9', border: '#2e7d32', text: '#2e7d32' },
  Cancelled:  { bg: '#ffebee', border: '#c62828', text: '#c62828' },
  Postponed:  { bg: '#f3e5f5', border: '#6a1b9a', text: '#6a1b9a' },
};

const SurgeryPlannerPage = () => {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'Admin';
  const isDoctor = user.role === 'Doctor';

  const [viewMode, setViewMode] = useState('calendar'); // 'list' | 'calendar'
  const [surgeries, setSurgeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [prefillDateTime, setPrefillDateTime] = useState(null);
  const [error, setError] = useState('');

  // list view filters
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');

  // event detail popup for calendar
  const [eventPopup, setEventPopup] = useState({ open: false, surgery: null, anchor: null });

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterFrom) params.from = filterFrom;
      if (filterTo) params.to = filterTo;
      const res = await surgeryService.getAll(params);
      setSurgeries(res.data);
    } catch {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterFrom, filterTo]);

  useEffect(() => { loadData(); }, [loadData]);

  // Build FullCalendar events from surgeries
  const calendarEvents = surgeries.map(s => {
    const start = new Date(s.scheduledDateTime);
    const end = new Date(start.getTime() + (s.estimatedDurationMinutes || 60) * 60000);
    const colors = STATUS_COLORS[s.status] || STATUS_COLORS.Scheduled;
    return {
      id: String(s.id),
      title: `${s.surgeryName}${s.patient ? ` — ${s.patient.firstName} ${s.patient.lastName}` : ''}`,
      start,
      end,
      backgroundColor: colors.bg,
      borderColor: colors.border,
      textColor: colors.text,
      extendedProps: { surgery: s },
    };
  });

  const handleOpenDialog = (item = null, prefill = null) => {
    setEditingItem(item);
    setPrefillDateTime(prefill);
    setError('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setPrefillDateTime(null);
  };

  const handleSaved = () => {
    handleCloseDialog();
    loadData();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this surgery record?')) return;
    try {
      await surgeryService.delete(id);
      setEventPopup({ open: false, surgery: null });
      loadData();
    } catch {
      alert('Failed to delete surgery');
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await surgeryService.updateStatus(id, newStatus);
      setEventPopup({ open: false, surgery: null });
      loadData();
    } catch {
      alert('Failed to update status');
    }
  };

  // FullCalendar callbacks
  const handleEventClick = ({ event }) => {
    const surgery = event.extendedProps.surgery;
    setEventPopup({ open: true, surgery });
  };

  const handleDateClick = ({ dateStr, date }) => {
    // dateStr is ISO string for the clicked slot
    handleOpenDialog(null, date);
  };

  // list view columns
  const columns = [
    { field: 'id', headerName: 'ID', width: 60 },
    {
      field: 'scheduledDateTime', headerName: 'Date & Time', width: 160,
      valueFormatter: (params) => {
        if (!params.value) return '';
        return new Date(params.value).toLocaleString('en-IN', {
          day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
        });
      },
    },
    {
      field: 'patient', headerName: 'Patient', flex: 1, minWidth: 140,
      valueGetter: (p) => p.row.patient ? `${p.row.patient.firstName} ${p.row.patient.lastName}` : '—',
    },
    { field: 'surgeryName', headerName: 'Surgery', flex: 1, minWidth: 160 },
    {
      field: 'surgeon', headerName: 'Surgeon', flex: 1, minWidth: 140,
      valueGetter: (p) => p.row.surgeon ? `Dr. ${p.row.surgeon.firstName} ${p.row.surgeon.lastName}` : '—',
    },
    {
      field: 'otRoom', headerName: 'OT Room', width: 110,
      valueGetter: (p) => p.row.otRoom?.name || '—',
    },
    {
      field: 'estimatedDurationMinutes', headerName: 'Duration', width: 90,
      valueFormatter: (p) => p.value ? `${p.value} min` : '—',
    },
    {
      field: 'status', headerName: 'Status', width: 130,
      renderCell: (p) => {
        const c = STATUS_COLORS[p.value] || {};
        return (
          <Box sx={{ px: 1.5, py: 0.3, borderRadius: 1, bgcolor: c.bg, color: c.text, fontSize: '0.75rem', fontWeight: 600 }}>
            {p.value}
          </Box>
        );
      },
    },
    {
      field: 'actions', headerName: 'Actions', width: 220, sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'nowrap' }}>
          <Tooltip title="View Detail">
            <IconButton size="small" color="info" onClick={() => navigate(`/surgery-planner/${params.row.id}`)}>
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenDialog(params.row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          {(isAdmin || isDoctor) && params.row.status === 'Scheduled' && (
            <Button size="small" variant="outlined" color="warning" sx={{ minWidth: 0, px: 1, fontSize: '0.65rem' }}
              onClick={() => handleStatusChange(params.row.id, 'InProgress')}>Start</Button>
          )}
          {(isAdmin || isDoctor) && params.row.status === 'InProgress' && (
            <Button size="small" variant="outlined" color="success" sx={{ minWidth: 0, px: 1, fontSize: '0.65rem' }}
              onClick={() => handleStatusChange(params.row.id, 'Completed')}>Done</Button>
          )}
          {isAdmin && (
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => handleDelete(params.row.id)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ];

  const { surgery: popupSurgery } = eventPopup;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Surgery Planner" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Top toolbar */}
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            {/* View toggle */}
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={(_, v) => { if (v) setViewMode(v); }}
              size="small"
            >
              <ToggleButton value="calendar">
                <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} /> Calendar
              </ToggleButton>
              <ToggleButton value="list">
                <TableRows fontSize="small" sx={{ mr: 0.5 }} /> List
              </ToggleButton>
            </ToggleButtonGroup>

            {/* Filters (list view only) */}
            {viewMode === 'list' && (
              <>
                <FilterList color="action" />
                <TextField
                  label="From" type="date" size="small" sx={{ width: 155 }}
                  InputLabelProps={{ shrink: true }} value={filterFrom}
                  onChange={(e) => setFilterFrom(e.target.value)}
                />
                <TextField
                  label="To" type="date" size="small" sx={{ width: 155 }}
                  InputLabelProps={{ shrink: true }} value={filterTo}
                  onChange={(e) => setFilterTo(e.target.value)}
                />
                <FormControl size="small" sx={{ minWidth: 140 }}>
                  <InputLabel>Status</InputLabel>
                  <Select value={filterStatus} label="Status" onChange={(e) => setFilterStatus(e.target.value)}>
                    <MenuItem value="">All</MenuItem>
                    {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
                <Button variant="outlined" startIcon={<Refresh />} size="small"
                  onClick={() => { setFilterFrom(''); setFilterTo(''); setFilterStatus(''); }}>
                  Clear
                </Button>
              </>
            )}

            <Box sx={{ flex: 1 }} />

            {/* Legend */}
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              {STATUS_OPTIONS.map(s => (
                <Box key={s} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: STATUS_COLORS[s].border }} />
                  <Typography variant="caption" color="text.secondary">{s}</Typography>
                </Box>
              ))}
            </Box>

            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenDialog()}>
              Schedule Surgery
            </Button>
          </Box>
        </Paper>

        {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
              initialView="timeGridWeek"
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay',
              }}
              events={calendarEvents}
              eventClick={handleEventClick}
              dateClick={handleDateClick}
              eventContent={(arg) => (
                <div style={{
                  padding: '1px 4px',
                  fontSize: '0.72rem',
                  lineHeight: '1.35',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  overflow: 'hidden',
                  width: '100%',
                }}>
                  {arg.timeText && <strong>{arg.timeText} </strong>}
                  {arg.event.title}
                </div>
              )}
              eventDidMount={(info) => {
                const s = info.event.extendedProps.surgery;
                if (s) {
                  const time = new Date(s.scheduledDateTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true });
                  const patient = s.patient ? `${s.patient.firstName} ${s.patient.lastName}` : '—';
                  const surgeon = s.surgeon ? `Dr. ${s.surgeon.firstName} ${s.surgeon.lastName}` : '—';
                  info.el.title = `${s.surgeryName}\nTime: ${time}\nPatient: ${patient}\nSurgeon: ${surgeon}\nStatus: ${s.status}`;
                }
              }}
              height="auto"
              slotMinTime="00:00:00"
              slotMaxTime="24:00:00"
              allDaySlot={false}
              nowIndicator
              businessHours={{ daysOfWeek: [1, 2, 3, 4, 5, 6], startTime: '08:00', endTime: '20:00' }}
              eventDisplay="block"
              eventTimeFormat={{ hour: '2-digit', minute: '2-digit', hour12: true }}
              slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: true }}
              buttonText={{ today: 'Today', month: 'Month', week: 'Week', day: 'Day' }}
            />
          </Paper>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <DataGrid
              rows={surgeries}
              columns={columns}
              loading={loading}
              pageSize={15}
              rowsPerPageOptions={[15, 30, 50]}
              autoHeight
              rowHeight={56}
              disableSelectionOnClick
              sx={{ '& .MuiDataGrid-cell': { overflow: 'visible' } }}
            />
          </Paper>
        )}
      </Container>

      {/* Calendar event detail popup */}
      <Dialog
        open={eventPopup.open}
        onClose={() => setEventPopup({ open: false, surgery: null })}
        maxWidth="sm"
        fullWidth
      >
        {popupSurgery && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{popupSurgery.surgeryName}</Typography>
                  <Box sx={{
                    display: 'inline-block', px: 1.5, py: 0.3, borderRadius: 1, mt: 0.5,
                    bgcolor: STATUS_COLORS[popupSurgery.status]?.bg,
                    color: STATUS_COLORS[popupSurgery.status]?.text,
                    fontSize: '0.75rem', fontWeight: 600,
                  }}>
                    {popupSurgery.status}
                  </Box>
                </Box>
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Patient</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {popupSurgery.patient ? `${popupSurgery.patient.firstName} ${popupSurgery.patient.lastName}` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Primary Surgeon</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {popupSurgery.surgeon ? `Dr. ${popupSurgery.surgeon.firstName} ${popupSurgery.surgeon.lastName}` : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">OT Room</Typography>
                  <Typography variant="body2">{popupSurgery.otRoom?.name || '—'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Duration</Typography>
                  <Typography variant="body2">{popupSurgery.estimatedDurationMinutes} min</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Scheduled</Typography>
                  <Typography variant="body2">
                    {new Date(popupSurgery.scheduledDateTime).toLocaleString('en-IN', {
                      weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </Typography>
                </Grid>
                {popupSurgery.teamMembers?.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Surgical Team</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {popupSurgery.teamMembers.map((tm, i) => (
                        <Chip key={i} size="small" label={`${tm.role}: ${tm.user?.firstName} ${tm.user?.lastName}`} variant="outlined" />
                      ))}
                    </Box>
                  </Grid>
                )}
                {popupSurgery.notes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="text.secondary">Notes</Typography>
                    <Typography variant="body2">{popupSurgery.notes}</Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(isAdmin || isDoctor) && popupSurgery.status === 'Scheduled' && (
                  <Button variant="outlined" color="warning" size="small"
                    onClick={() => handleStatusChange(popupSurgery.id, 'InProgress')}>
                    Mark In Progress
                  </Button>
                )}
                {(isAdmin || isDoctor) && popupSurgery.status === 'InProgress' && (
                  <Button variant="outlined" color="success" size="small"
                    onClick={() => handleStatusChange(popupSurgery.id, 'Completed')}>
                    Mark Completed
                  </Button>
                )}
                {(isAdmin || isDoctor) && ['Scheduled', 'InProgress'].includes(popupSurgery.status) && (
                  <Button variant="outlined" color="error" size="small"
                    onClick={() => handleStatusChange(popupSurgery.id, 'Cancelled')}>
                    Cancel
                  </Button>
                )}
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isAdmin && (
                  <Button size="small" color="error" onClick={() => handleDelete(popupSurgery.id)}>
                    Delete
                  </Button>
                )}
                <Button size="small" variant="outlined"
                  onClick={() => { setEventPopup({ open: false, surgery: null }); handleOpenDialog(popupSurgery); }}>
                  Edit
                </Button>
                <Button size="small" variant="contained" startIcon={<OpenInNew />}
                  onClick={() => navigate(`/surgery-planner/${popupSurgery.id}`)}>
                  Open Detail
                </Button>
                <Button size="small" onClick={() => setEventPopup({ open: false, surgery: null })}>
                  Close
                </Button>
              </Box>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create / Edit Dialog */}
      <SurgeryFormDialog
        open={openDialog}
        editingItem={editingItem}
        prefillDateTime={prefillDateTime}
        onClose={handleCloseDialog}
        onSaved={handleSaved}
      />
    </div>
  );
};

export default SurgeryPlannerPage;
